import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { normalizeSeasonDatePresets } from "@/lib/seasonDatePresets";

export function AppSettings() {
  const [settings, setSettings] = useState({
    title: "",
    theme: {},
    nombre_empresa: "",
    calle_empresa: "",
    poblacion_empresa: "",
    provincia_empresa: "",
    cif_empresa: "",
    logo_empresa: "",
    default_temporada_products: null,
    default_picking_season_preset_id: null,
    season_date_ranges: []
  });
  const [availableTemporadas, setAvailableTemporadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .not('title', 'is', null)
        .limit(1);

      if (error) throw error;
      
      if (data && data.length > 0) {
        setSettings({
          ...data[0],
          season_date_ranges: normalizeSeasonDatePresets(data[0].season_date_ranges || [])
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los ajustes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTemporadas = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('temporada')
        .not('temporada', 'is', null)
        .not('temporada', 'eq', '');

      if (error) throw error;

      const uniqueTemporadas = [...new Set((data || []).map((item) => item.temporada))].sort();
      setAvailableTemporadas(uniqueTemporadas);
    } catch (error) {
      console.error('Error fetching temporadas:', error);
      setAvailableTemporadas([]);
    }
  };

  useEffect(() => {
    fetchAvailableTemporadas();
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Verificar el tamaño del archivo (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "El tamaño máximo del logo debe ser 2MB",
        variant: "destructive"
      });
      return;
    }

    // Verificar el tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "El archivo debe ser una imagen",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSettings(prev => ({
        ...prev,
        logo_empresa: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { data: existingData, error: fetchError } = await supabase
        .from('app_settings')
        .select('id')
        .not('title', 'is', null)
        .limit(1);

      if (fetchError) throw fetchError;

      const settingsData = {
        title: settings.title,
        theme: settings.theme,
        nombre_empresa: settings.nombre_empresa,
        calle_empresa: settings.calle_empresa,
        poblacion_empresa: settings.poblacion_empresa,
        provincia_empresa: settings.provincia_empresa,
        cif_empresa: settings.cif_empresa,
        logo_empresa: settings.logo_empresa,
        default_temporada_products: settings.default_temporada_products,
        default_picking_season_preset_id: settings.default_picking_season_preset_id,
        season_date_ranges: normalizeSeasonDatePresets(settings.season_date_ranges)
      };

      if (existingData && existingData.length > 0) {
        const { error: updateError } = await supabase
          .from('app_settings')
          .update(settingsData)
          .eq('id', existingData[0].id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('app_settings')
          .insert(settingsData);

        if (insertError) throw insertError;
      }

      toast({
        title: "Éxito",
        description: "Ajustes guardados correctamente"
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "No se pudieron guardar los ajustes",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  const addSeasonDateRange = () => {
    const timestamp = Date.now();
    setSettings(prev => ({
      ...prev,
      season_date_ranges: [
        ...(prev.season_date_ranges || []),
        { id: `season_${timestamp}`, name: "", from: "", to: "", active: true }
      ]
    }));
  };

  const updateSeasonDateRange = (index, field, value) => {
    setSettings(prev => {
      const list = [...(prev.season_date_ranges || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, season_date_ranges: list };
    });
  };

  const removeSeasonDateRange = (index) => {
    setSettings(prev => {
      const list = [...(prev.season_date_ranges || [])];
      list.splice(index, 1);
      return { ...prev, season_date_ranges: list };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-lg font-medium mb-4">Ajustes de la Aplicación</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Título de la Aplicación
            </label>
            <Input
              value={settings.title}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                title: e.target.value
              }))}
              placeholder="Mi Aplicación"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Temporada por defecto (Productos)
            </label>
            <Select
              value={settings.default_temporada_products || "__none__"}
              onValueChange={(value) => setSettings(prev => ({
                ...prev,
                default_temporada_products: value === "__none__" ? null : value
              }))}
            >
              <SelectTrigger data-season-selector="true">
                <SelectValue placeholder="Sin filtro por defecto" />
              </SelectTrigger>
              <SelectContent data-season-selector="true">
                <SelectItem value="__none__">Sin filtro por defecto</SelectItem>
                {settings.default_temporada_products &&
                  !availableTemporadas.includes(settings.default_temporada_products) && (
                    <SelectItem value={settings.default_temporada_products}>
                      {settings.default_temporada_products} (no disponible)
                    </SelectItem>
                  )}
                {availableTemporadas.map((temporada) => (
                  <SelectItem key={temporada} value={temporada}>
                    {temporada}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500 mt-1">
              Se aplicará automáticamente al abrir el módulo de productos y los selectores de producto.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Temporada por defecto (Picking pedidos)
            </label>
            <Select
              value={settings.default_picking_season_preset_id || "__none__"}
              onValueChange={(value) => setSettings(prev => ({
                ...prev,
                default_picking_season_preset_id: value === "__none__" ? null : value
              }))}
            >
              <SelectTrigger data-season-selector="true">
                <SelectValue placeholder="Sin temporada por defecto" />
              </SelectTrigger>
              <SelectContent data-season-selector="true">
                <SelectItem value="__none__">Sin temporada por defecto</SelectItem>
                {settings.default_picking_season_preset_id &&
                  !(settings.season_date_ranges || []).some((season) => season.id === settings.default_picking_season_preset_id) && (
                    <SelectItem value={settings.default_picking_season_preset_id}>
                      {settings.default_picking_season_preset_id} (no disponible)
                    </SelectItem>
                  )}
                {(settings.season_date_ranges || []).map((season) => (
                  <SelectItem key={season.id} value={season.id}>
                    {season.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500 mt-1">
              Se aplicará automáticamente al abrir la gestión previa al picking usando las temporadas configuradas por rango de fechas.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">
                Temporadas para filtros de fechas (Pedidos/Entradas)
              </label>
              <Button type="button" variant="outline" size="sm" onClick={addSeasonDateRange}>
                Añadir temporada
              </Button>
            </div>
            <div className="space-y-2">
              {(settings.season_date_ranges || []).length === 0 && (
                <p className="text-sm text-gray-500">No hay temporadas configuradas.</p>
              )}
              {(settings.season_date_ranges || []).map((season, index) => (
                <div key={season.id || `season_${index}`} className="grid grid-cols-12 gap-2 items-end border rounded p-2">
                  <div className="col-span-4">
                    <label className="block text-xs text-gray-600 mb-1">Nombre</label>
                    <Input
                      value={season.name || ""}
                      onChange={(e) => updateSeasonDateRange(index, "name", e.target.value)}
                      placeholder="Ej. Verano 2026"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs text-gray-600 mb-1">Desde</label>
                    <Input
                      type="date"
                      value={season.from || ""}
                      onChange={(e) => updateSeasonDateRange(index, "from", e.target.value)}
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs text-gray-600 mb-1">Hasta</label>
                    <Input
                      type="date"
                      value={season.to || ""}
                      onChange={(e) => updateSeasonDateRange(index, "to", e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full text-red-600 hover:text-red-700"
                      onClick={() => removeSeasonDateRange(index)}
                    >
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Estas temporadas aparecen como atajos para rellenar fechas Desde/Hasta con un clic.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Logo de la Empresa
            </label>
            <div className="flex items-center space-x-4">
              {settings.logo_empresa && (
                <div className="relative w-20 h-20">
                  <img 
                    src={settings.logo_empresa} 
                    alt="Logo empresa" 
                    className="w-full h-full object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => setSettings(prev => ({ ...prev, logo_empresa: "" }))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="flex-1">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="max-w-xs"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Tamaño máximo: 2MB. Formatos: JPG, PNG, GIF
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Nombre de la Empresa
            </label>
            <Input
              value={settings.nombre_empresa}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                nombre_empresa: e.target.value
              }))}
              placeholder="Nombre de la empresa"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Calle
            </label>
            <Input
              value={settings.calle_empresa}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                calle_empresa: e.target.value
              }))}
              placeholder="Dirección de la empresa"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Población
              </label>
              <Input
                value={settings.poblacion_empresa}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  poblacion_empresa: e.target.value
                }))}
                placeholder="Población"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Provincia
              </label>
              <Input
                value={settings.provincia_empresa}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  provincia_empresa: e.target.value
                }))}
                placeholder="Provincia"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              CIF/NIF
            </label>
            <Input
              value={settings.cif_empresa}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                cif_empresa: e.target.value
              }))}
              placeholder="CIF/NIF de la empresa"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Tema Principal
            </label>
            <div className="grid grid-cols-3 gap-4">
              <Button
                type="button"
                variant={settings.theme?.name === 'light' ? 'default' : 'outline'}
                onClick={() => setSettings(prev => ({
                  ...prev,
                  theme: { name: 'light' }
                }))}
              >
                Claro
              </Button>
              <Button
                type="button"
                variant={settings.theme?.name === 'dark' ? 'default' : 'outline'}
                onClick={() => setSettings(prev => ({
                  ...prev,
                  theme: { name: 'dark' }
                }))}
              >
                Oscuro
              </Button>
              <Button
                type="button"
                variant={settings.theme?.name === 'system' ? 'default' : 'outline'}
                onClick={() => setSettings(prev => ({
                  ...prev,
                  theme: { name: 'system' }
                }))}
              >
                Sistema
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit">
          Guardar Cambios
        </Button>
      </div>
    </form>
  );
}
